import { useState } from "react"
import { toast } from "sonner"
import { AppShell } from "@/components/layout/app-shell"
import { PageHeader } from "@/components/layout/page-header"
import { Card } from "@/components/ui/card"
import { cn } from "@/lib/cn"
import { DocumentRenderer } from "@/components/ui-document/document-renderer"
import type {
  ActionPayload,
  RefreshEnvelope,
  V1Document,
} from "@/components/ui-document/types"

// Settings → UI Document blocks. Internal review-only page that renders
// every v1 block type with hardcoded fixtures, behind a width selector
// so block container queries fire as they would in the production
// sidebar (~280–400px). Bypasses the App pipeline entirely — no fetch,
// no validator round-trip — just a direct mount of <DocumentRenderer>.
//
// Add fixtures below to extend coverage. Action callbacks are no-ops
// that toast + console.log (production proxies through Valet to the
// partner; here we never leave the page).

type Width = 320 | 480 | 800 | "full"

const WIDTHS: { value: Width; label: string }[] = [
  { value: 320,    label: "320 px (sidebar)" },
  { value: 480,    label: "480 px" },
  { value: 800,    label: "800 px" },
  { value: "full", label: "Full width" },
]

async function noopAction(action: ActionPayload, _refresh?: string[]): Promise<RefreshEnvelope | null> {
  // eslint-disable-next-line no-console
  console.log("[ui-document-demo] action invoked", action)
  toast.info(`Demo · would ${action.method} ${action.path}`)
  return { scope: "none" }
}

const HEADER_FIXTURE: V1Document = {
  version: 1,
  blocks: [
    { type: "header", text: "Customer overview" },
    { type: "divider" },
    { type: "header", text: "Customer overview", subtitle: "VIP since Aug 2024 · 47 lifetime tickets" },
  ],
}

const DIVIDER_FIXTURE: V1Document = {
  version: 1,
  blocks: [
    { type: "text", content: "Above the divider" },
    { type: "divider" },
    { type: "text", content: "Below the divider" },
  ],
}

const KEY_VALUE_FIXTURE: V1Document = {
  version: 1,
  blocks: [
    { type: "header", text: "1 column" },
    {
      type: "key_value",
      columns: 1,
      items: [
        { label: "Name",   value: "Jamie Rivera" },
        { label: "Email",  value: "jamie@example.com",   copyable: true },
        { label: "Status", value: "Active",              badge: "success" },
        { label: "Token",  value: "tok_4f1a8b...c9e2",   mono: true, copyable: true },
        { label: "SSN",    value: "***-**-1234",         redact: true },
      ],
    },
    { type: "header", text: "2 columns" },
    {
      type: "key_value",
      columns: 2,
      items: [
        { label: "First name",  value: "Jamie" },
        { label: "Last name",   value: "Rivera" },
        { label: "Plan",        value: "Pro",          badge: "info" },
        { label: "Auto-renew",  value: true },
        { label: "Trial ends",  value: "Jun 14, 2026" },
        { label: "Locale",      value: "en-US" },
      ],
    },
  ],
}

const BADGE_ROW_FIXTURE: V1Document = {
  version: 1,
  blocks: [
    {
      type: "badge_row",
      badges: [
        { label: "Neutral", tone: "neutral" },
        { label: "Success", tone: "success" },
        { label: "Warning", tone: "warning" },
        { label: "Danger",  tone: "danger"  },
        { label: "Info",    tone: "info"    },
      ],
    },
  ],
}

const METRIC_GRID_FIXTURE: V1Document = {
  version: 1,
  blocks: [
    { type: "header", text: "2 columns" },
    {
      type: "metric_grid",
      columns: 2,
      metrics: [
        { label: "Open tickets", value: 12,       sub: "down 4 vs last week", tone: "success" },
        { label: "Avg response", value: "2h 14m", sub: "SLA: 4h" },
      ],
    },
    { type: "header", text: "3 columns, mixed tones" },
    {
      type: "metric_grid",
      columns: 3,
      metrics: [
        { label: "Resolved",     value: 84, tone: "success" },
        { label: "Pending",      value: 7,  tone: "warning" },
        { label: "Breached SLA", value: 2,  tone: "danger"  },
      ],
    },
    { type: "header", text: "4 columns" },
    {
      type: "metric_grid",
      columns: 4,
      metrics: [
        { label: "MRR",   value: "$2,400", sub: "USD" },
        { label: "Tier",  value: "Pro",    tone: "info" },
        { label: "Seats", value: 12 },
        { label: "Used",  value: "11 / 12", tone: "warning" },
      ],
    },
  ],
}

const TEXT_FIXTURE: V1Document = {
  version: 1,
  blocks: [
    { type: "text", content: "Default text — body copy and explanations the operator should read." },
    { type: "text", content: "Muted text — secondary context that shouldn't pull focus.",   tone: "muted" },
    { type: "text", content: "Warning text — heads-up about something the operator should notice.", tone: "warning" },
    { type: "text", content: "Danger text — surfaces a hard error or blocked condition.",  tone: "danger" },
  ],
}

const TABLE_FIXTURE: V1Document = {
  version: 1,
  blocks: [
    { type: "header", text: "Basic columns (text + date + duration_ms)" },
    {
      type: "table",
      columns: [
        { key: "id",      label: "ID",      type: "text",        align: "left",  priority: 1 },
        { key: "title",   label: "Subject", type: "text",        align: "left",  priority: 1 },
        { key: "opened",  label: "Opened",  type: "date",        align: "right", priority: 2 },
        { key: "elapsed", label: "Elapsed", type: "duration_ms", align: "right", priority: 3 },
      ],
      rows: [
        { id: "T-481", title: "Refund delayed", opened: "2026-04-22", elapsed: 1_220_000 },
        { id: "T-482", title: "Login broken",   opened: "2026-04-23", elapsed: 7_400_000 },
        { id: "T-483", title: "Billing error",  opened: "2026-04-24", elapsed: 92_000 },
      ],
    },

    { type: "header", text: "Money + badge + datetime cells" },
    {
      type: "table",
      columns: [
        { key: "plan",   label: "Plan",   type: "badge",     align: "left",  priority: 1 },
        { key: "amount", label: "Amount", type: "money_usd", align: "right", priority: 1 },
        { key: "when",   label: "When",   type: "datetime",  align: "right", priority: 2 },
      ],
      rows: [
        { plan: { type: "badge", value: { label: "Pro",   tone: "info" } },    amount: 2499, when: "2026-04-22T14:33:00Z" },
        { plan: { type: "badge", value: { label: "Free",  tone: "neutral" } }, amount: 0,    when: "2026-04-21T08:12:00Z" },
        { plan: { type: "badge", value: { label: "Trial", tone: "warning" } }, amount: 0,    when: "2026-04-20T19:55:00Z" },
      ],
    },

    { type: "header", text: "With action cells" },
    {
      type: "table",
      columns: [
        { key: "label", label: "Action item", type: "text",   align: "left",  priority: 1 },
        { key: "do_it", label: "",            type: "action", align: "right", priority: 1 },
      ],
      rows: [
        { label: "Resend confirmation email", do_it: { type: "action", value: { label: "Resend",  method: "POST",   path: "/internal/demo/resend",  tone: "info"   } } },
        { label: "Reset password",            do_it: { type: "action", value: { label: "Reset",   method: "POST",   path: "/internal/demo/reset",   confirm: "Reset this user's password?" } } },
        { label: "Disable account",           do_it: { type: "action", value: { label: "Disable", method: "DELETE", path: "/internal/demo/disable", tone: "danger", confirm: "Disable account?" } } },
      ],
    },

    { type: "header", text: "Empty state (empty_text)" },
    {
      type: "table",
      columns: [
        { key: "id",   label: "ID",   type: "text", priority: 1 },
        { key: "name", label: "Name", type: "text", priority: 1 },
      ],
      rows: [],
      empty_text: "No matching records.",
    },
  ],
}

const LINK_ROW_FIXTURE: V1Document = {
  version: 1,
  blocks: [
    {
      type: "link_row",
      links: [
        { label: "Internal: settings",   href: "/settings",                        tone: "neutral" },
        { label: "Stripe customer",      href: "https://dashboard.stripe.com/c/x", tone: "info" },
        { label: "Salesforce account",   href: "https://salesforce.example/acct",  tone: "success" },
        { label: "Triggered alert",      href: "https://example.com/alert",        tone: "warning" },
        { label: "Dispute on file",      href: "https://example.com/dispute",      tone: "danger" },
      ],
    },
  ],
}

const ACTION_BUTTON_FIXTURE: V1Document = {
  version: 1,
  blocks: [
    { type: "header", text: "HTTP methods + tones" },
    { type: "action_button", label: "GET status",     method: "GET",    path: "/internal/demo/status" },
    { type: "action_button", label: "POST refresh",   method: "POST",   path: "/internal/demo/refresh", tone: "info" },
    { type: "action_button", label: "PATCH plan",     method: "PATCH",  path: "/internal/demo/plan",    tone: "warning" },
    { type: "action_button", label: "DELETE account", method: "DELETE", path: "/internal/demo/account", tone: "danger", confirm: "Delete this account permanently?" },
    { type: "header", text: "With refresh ids" },
    { type: "action_button", label: "Refresh nearby", method: "POST",   path: "/internal/demo/refresh", refresh: ["overview_metrics", "recent_tickets"] },
  ],
}

const ACTION_ROW_FIXTURE: V1Document = {
  version: 1,
  blocks: [
    {
      type: "action_row",
      actions: [
        { label: "Approve", method: "POST", path: "/internal/demo/approve", tone: "success" },
        { label: "Snooze",  method: "POST", path: "/internal/demo/snooze",  tone: "neutral" },
        { label: "Decline", method: "POST", path: "/internal/demo/decline", tone: "danger", confirm: "Decline?" },
      ],
    },
  ],
}

const EMPTY_STATE_FIXTURE: V1Document = {
  version: 1,
  blocks: [
    { type: "empty_state", text: "No history yet — this user is brand new." },
    { type: "empty_state", text: "No notes attached to this customer.", icon: "sticky-note" },
  ],
}

const COLLAPSIBLE_FIXTURE: V1Document = {
  version: 1,
  blocks: [
    { type: "header", text: "Default open" },
    {
      type: "collapsible",
      title: "Recent activity",
      default_open: true,
      blocks: [
        { type: "key_value", items: [
          { label: "Last seen",  value: "5 minutes ago" },
          { label: "Last login", value: "2026-05-02 14:33 UTC" },
        ] },
      ],
    },
    { type: "header", text: "Default closed" },
    {
      type: "collapsible",
      title: "Raw debug payload",
      default_open: false,
      blocks: [
        { type: "text", content: "Click to expand." },
        { type: "text", content: "{ \"trace_id\": \"abc-123\", \"span\": 17 }", tone: "muted" },
      ],
    },
    { type: "header", text: "Nested with mixed blocks" },
    {
      type: "collapsible",
      title: "Account details",
      default_open: true,
      blocks: [
        { type: "badge_row", badges: [
          { label: "Verified", tone: "success" },
          { label: "MFA on",   tone: "info"    },
        ] },
        { type: "key_value", columns: 2, items: [
          { label: "Plan",  value: "Pro",      badge: "info" },
          { label: "Seats", value: "12 / 25" },
        ] },
        { type: "action_row", actions: [
          { label: "Open in admin", method: "GET",  path: "/internal/demo/admin", tone: "neutral" },
          { label: "Send email",    method: "POST", path: "/internal/demo/email", tone: "info" },
        ] },
      ],
    },
  ],
}

const CARD_LIST_FIXTURE: V1Document = {
  version: 1,
  blocks: [
    { type: "header", text: "Cards with badges + subtitles + fields" },
    {
      type: "card_list",
      items: [
        {
          title:    "Order #4982",
          subtitle: "Shipped Apr 22",
          badge:    { label: "Delivered", tone: "success" },
          fields: [
            { label: "Items",    value: 3 },
            { label: "Total",    value: "$84.50" },
            { label: "Carrier",  value: "USPS" },
            { label: "Tracking", value: "9400 1234 5678", mono: true },
          ],
        },
        {
          title:    "Order #4981",
          subtitle: "Awaiting payment",
          badge:    { label: "Pending", tone: "warning" },
          fields: [
            { label: "Items", value: 1 },
            { label: "Total", value: "$24.99" },
          ],
        },
        {
          title:    "Order #4980",
          badge:    { label: "Refunded", tone: "danger" },
          fields: [
            { label: "Reason", value: "Damaged in transit" },
            { label: "Refund", value: "$129.00" },
          ],
        },
      ],
    },
    { type: "header", text: "max_items truncation" },
    {
      type: "card_list",
      max_items: 2,
      items: [
        { fields: [{ label: "First",                value: 1 }] },
        { fields: [{ label: "Second",               value: 2 }] },
        { fields: [{ label: "Third (truncated)",    value: 3 }] },
        { fields: [{ label: "Fourth (truncated)",   value: 4 }] },
      ],
    },
    { type: "header", text: "Empty list (empty_text shown)" },
    {
      type: "card_list",
      items:      [],
      empty_text: "No orders found.",
    },
  ],
}

type Section = {
  key:         string
  label:       string
  description: string
  doc:         V1Document
}

const SECTIONS: Section[] = [
  { key: "header",        label: "header",        description: "Section title with optional subtitle.",                                                       doc: HEADER_FIXTURE },
  { key: "divider",       label: "divider",       description: "Horizontal rule.",                                                                              doc: DIVIDER_FIXTURE },
  { key: "key_value",     label: "key_value",     description: "Label-value list. 1 or 2 columns. Item modifiers: mono, badge, redact, copyable.",              doc: KEY_VALUE_FIXTURE },
  { key: "badge_row",     label: "badge_row",     description: "Inline pills. Tones: neutral / success / warning / danger / info.",                             doc: BADGE_ROW_FIXTURE },
  { key: "metric_grid",   label: "metric_grid",   description: "Multi-column metric tiles. 2/3/4 columns, optional sub-label and tone.",                        doc: METRIC_GRID_FIXTURE },
  { key: "text",          label: "text",          description: "Body copy. Tones: default, muted, warning, danger.",                                            doc: TEXT_FIXTURE },
  { key: "table",         label: "table",         description: "Dense rows. Cell types: text, money_usd, date, datetime, duration_ms, badge, link, action.",    doc: TABLE_FIXTURE },
  { key: "link_row",      label: "link_row",      description: "Inline links across all five tones.",                                                           doc: LINK_ROW_FIXTURE },
  { key: "action_button", label: "action_button", description: "Single-action button. All HTTP methods, with confirm dialog and partial-refresh ids.",          doc: ACTION_BUTTON_FIXTURE },
  { key: "action_row",    label: "action_row",    description: "Three actions side-by-side.",                                                                   doc: ACTION_ROW_FIXTURE },
  { key: "empty_state",   label: "empty_state",   description: "Dashed-border placeholder for no-data sections. (Renderer ignores `icon` in v1.)",              doc: EMPTY_STATE_FIXTURE },
  { key: "collapsible",   label: "collapsible",   description: "Show/hide section. default_open + nested arbitrary blocks.",                                    doc: COLLAPSIBLE_FIXTURE },
  { key: "card_list",     label: "card_list",     description: "Stacked cards (alternative to table for narrow layouts). Title, subtitle, badge, field list.", doc: CARD_LIST_FIXTURE },
]

export default function SettingsUiDocumentBlocks() {
  const [width, setWidth] = useState<Width>(320)

  return (
    <AppShell>
      <PageHeader
        eyebrow="Settings"
        title="UI Document blocks"
        subtitle="Internal review page. Every v1 block type rendered with hardcoded fixtures. Toggle widths to mirror the production sidebar."
      />

      <div className="mb-6 flex flex-wrap items-center gap-2">
        <span className="text-xs uppercase tracking-wide text-app-muted">Width:</span>
        {WIDTHS.map((w) => (
          <button
            key={String(w.value)}
            type="button"
            onClick={() => setWidth(w.value)}
            className={cn(
              "rounded-control border px-3 py-1.5 text-xs font-medium transition",
              width === w.value
                ? "border-brand-red bg-brand-red/10 text-app-text"
                : "border-border text-app-muted hover:bg-white/5 hover:text-app-text",
            )}
          >
            {w.label}
          </button>
        ))}
      </div>

      <div className="space-y-8">
        {SECTIONS.map((s) => (
          <BlockSection key={s.key} section={s} width={width} />
        ))}
      </div>
    </AppShell>
  )
}

function BlockSection({ section, width }: { section: Section; width: Width }) {
  return (
    <section>
      <div className="mb-3">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-app-text">{section.label}</h2>
        <p className="mt-0.5 text-xs text-app-muted">{section.description}</p>
      </div>
      <div
        className={cn(width === "full" && "w-full")}
        style={width === "full" ? undefined : { width }}
      >
        <Card variant="inset" className="p-4">
          <DocumentRenderer document={section.doc} onAction={noopAction} />
        </Card>
      </div>
    </section>
  )
}
